import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import {
  PageContent,
  Spinner,
  Table,
  CisContainer,
  Button,
  Row,
  ButtonIcon,
  Modal,
} from 'components';
import PropTypes from 'prop-types';
import {
  loadIpmaCisCreator,
  setIpmaCisPageCreator,
  setIpmaCisPerPageCreator,
  setIpmaCisSortCreator,
  setIpmaCisFiltersCreator,
  removeIpmaCisFiltersCreator,
  setIpmaCisProfileCreator,
} from 'store/actionsCreators';
import { isLoading, globalPropTypes, checkPermissions } from 'helpers';
import {
  loadGlobalCisInvoicesAttributesCreator,
  removeIpmaCisCreator,
} from '../../../../store/actionsCreators';
import { printInvoiceSummary } from '../../../../api/services/cis';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import { formatDate } from 'helpers/date';

const Invoices = ({
  loading,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  setProfile,
  removeInvoice,
  countries,
  ma,
  loadInvoicesAttributes,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();
  const [portalReady, setPortalReady] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loadingSummaryId, setLoadingSummaryId] = useState(null);

  const manageCisPermission = checkPermissions({ roles: ['ipma_cis'] }, userPermissions || []);

  useEffect(() => {
    if (document.getElementById('ipma-cis-buttons-portal')) {
      setPortalReady(true);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  useEffect(() => {
    loadInvoicesAttributes();
  }, []);

  const handlePrintSummary = async (id) => {
    try {
      setLoadingSummaryId(id);
      await printInvoiceSummary(id);
    } catch (error) {
      console.error('Error printing invoice summary:', error);
    } finally {
      setLoadingSummaryId(null);
    }
  };

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.referenceNumber || ''}
                  onFilter={(val) => handleColumnFilterChange('referenceNumber', val)}
                  filterPlaceholder={t('page.user.cis.invoices.referenceNumberPlaceholder')}
                  label={t('page.user.cis.invoices.referenceNumber')}
                  sort
                  sortDirection={getSortDirection('referenceNumber')}
                  onSort={(dir) => handleColumnSort('referenceNumber', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.memberAssociation || ''}
                  onFilter={(val) => handleColumnFilterChange('memberAssociation', val)}
                  filterOptions={ma}
                  filterPlaceholder={t('page.user.cis.invoices.memberAssociationPlaceholder')}
                  label={t('page.user.cis.invoices.memberAssociation')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.clientMemberAssociation || ''}
                  onFilter={(val) => handleColumnFilterChange('clientMemberAssociation', val)}
                  filterOptions={ma}
                  filterPlaceholder={t('page.user.cis.invoices.clientMemberAssociationPlaceholder')}
                  label={t('page.user.cis.invoices.clientMemberAssociation')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.country || ''}
                  onFilter={(val) => handleColumnFilterChange('country', val)}
                  filterOptions={countries}
                  filterPlaceholder={t('page.user.cis.invoices.clientCountryPlaceholder')}
                  label={t('page.user.cis.invoices.clientCountry')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.invoiceDateFrom || ''}
                  filterValueTo={filters.invoiceDateTo || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('invoiceDateFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('invoiceDateTo', val)}
                  filterPlaceholder={t('page.user.cis.invoices.invoiceDatePlaceholder')}
                  label={t('page.user.cis.invoices.invoiceDate')}
                  sort
                  sortDirection={getSortDirection('invoiceDate')}
                  onSort={(dir) => handleColumnSort('invoiceDate', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.invoicePayedDateFrom || ''}
                  filterValueTo={filters.invoicePayedDateTo || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('invoicePayedDateFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('invoicePayedDateTo', val)}
                  filterPlaceholder={t('page.user.cis.invoices.invoicePayedDatePlaceholder')}
                  label={t('page.user.cis.invoices.invoicePayedDate')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.vatId || ''}
                  onFilter={(val) => handleColumnFilterChange('vatId', val)}
                  filterPlaceholder={t('page.user.cis.invoices.vatIdPlaceholder')}
                  label={t('page.user.cis.invoices.vatId')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker"
                  filterValue={filters.dateFrom || ''}
                  onFilter={(val) => handleColumnFilterChange('dateFrom', val)}
                  filterPlaceholder={t('page.user.cis.invoices.dateFromPlaceholder')}
                  label={t('page.user.cis.invoices.dateFrom')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker"
                  filterValue={filters.dateTo || ''}
                  onFilter={(val) => handleColumnFilterChange('dateTo', val)}
                  filterPlaceholder={t('page.user.cis.invoices.dateToPlaceholder')}
                  label={t('page.user.cis.invoices.dateTo')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyActionHeading style={{ minWidth: '10rem' }}>
                <TableHeaderCell
                  label={t('page.user.cis.certificates.actions')}
                  labelAlign="center"
                  isAction={true}
                />
              </CisTable.StickyActionHeading>
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={10}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={10}>{t('global.noResultsFound')}</CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.invoices.referenceNumber')}:</Table.Label>
                      {item.referenceNumber}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.invoices.memberAssociation')}:</Table.Label>
                      {item.memberAssociation?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.invoices.clientMemberAssociation')}:
                      </Table.Label>
                      {item.memberAssociation?.invoiceClients?.map((client) => client.initials).join(', ')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.invoices.clientCountry')}:</Table.Label>
                      {item.memberAssociation?.invoiceClients?.map((client) => client.country).join(', ')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.invoices.invoiceDate')}:</Table.Label>
                      {item.invoiceDate && formatDate(item.invoiceDate)}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.invoices.invoicePayedDate')}:</Table.Label>
                      {item.invoicePayedDate && formatDate(item.invoicePayedDate)}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.invoices.vatId')}:</Table.Label>
                      {item.vatId}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.invoices.dateFrom')}:</Table.Label>
                      {item.dateFrom && formatDate(item.dateFrom)}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.invoices.dateTo')}:</Table.Label>
                      {item.dateTo && formatDate(item.dateTo)}
                    </CisTable.Cell>
                    <CisTable.StickyActionCell style={{ minWidth: '9.6rem' }}>
                      <Table.Label>{t('page.user.cis.invoices.actions')}:</Table.Label>
                      <Row style={{ gap: '3px', justifyContent: 'center' }}>
                        {manageCisPermission && (
                          <>
                            <ButtonIcon
                              icon="edit"
                              secondary
                              as={Button}
                              onClick={() => {
                                setProfile(item);
                                history.push('/ipma/cis/invoices/edit');
                              }}
                            />
                            <ButtonIcon
                              icon="trash"
                              onClick={() => {
                                setOpenConfirmModal(true);
                                setSelectedItem(item);
                              }}
                            />
                          </>
                        )}

                        <ButtonIcon
                          icon="file"
                          secondary
                          as={Button}
                          loading={loadingSummaryId === item.id}
                          disabled={loadingSummaryId === item.id}
                          onClick={() => handlePrintSummary(item.id)}
                          title={t('global.export')}
                        />
                      </Row>
                    </CisTable.StickyActionCell>
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
      <Modal
        heading={t('page.user.cis.invoices.confirmDelete')}
        isOpen={openConfirmModal && selectedItem?.id}
        confirm={() => {
          removeInvoice(selectedItem.id);
          setOpenConfirmModal(false);
        }}
        cancel={() => setOpenConfirmModal(false)}
        onRequestClose={() => setOpenConfirmModal(false)}
      />
      {portalReady &&
        manageCisPermission &&
        ReactDOM.createPortal(
          <Button style={{ width: '30%' }} secondary as={Link} to="/ipma/cis/invoices/create">
            {t('page.user.cis.invoices.new')}
          </Button>,
          document.getElementById('ipma-cis-buttons-portal'),
        )}
    </PageContent>
  );
};

Invoices.propTypes = {
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  countries: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  ma: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  removeFilters: PropTypes.func.isRequired,
  setProfile: PropTypes.func.isRequired,
  removeInvoice: PropTypes.func.isRequired,
};

Invoices.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ loading, ipma, globals, user }) => ({
  loading: isLoading(loading, 'LOAD_IPMA_CIS'),
  list: ipma.cis.invoices,
  countries: globals.countries,
  ma: globals.availableMA,
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadIpmaCisCreator('invoices')),
  setPage: (payload) => dispatch(setIpmaCisPageCreator('invoices', payload)),
  setPerPage: (payload) => dispatch(setIpmaCisPerPageCreator('invoices', payload)),
  setSort: (payload) => dispatch(setIpmaCisSortCreator('invoices', payload)),
  setFilters: (payload) => dispatch(setIpmaCisFiltersCreator('invoices', payload)),
  removeFilters: () => dispatch(removeIpmaCisFiltersCreator('invoices')),
  setProfile: (payload) => dispatch(setIpmaCisProfileCreator('invoices', payload)),
  removeInvoice: (payload) => dispatch(removeIpmaCisCreator('invoices', payload)),
  loadInvoicesAttributes: () => dispatch(loadGlobalCisInvoicesAttributesCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Invoices);
